import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box, Typography, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { API_BASE_URL } from "../../config";
import {
  resetIntake,
  setIntakeProject,
  setIntakeSession,
  setIntakeStatus,
  setIntakeError,
  addIntakeMessage,
  setIntakeMessages,
  setScorePills,
  clearScorePills,
  applyIntakeState,
  clearIntakeTranscript,
  truncateIntakeFromMessage,
} from "../../store/slices/intakeSlice";
import {
  getIntakeSession,
  sendIntakeChat,
  pasteIntakeDocument,
  uploadIntakeFile,
  cancelIntakeUpload,
  fetchIntakeUploadStatus,
  pollUploadStatus,
  fetchIntakeExtraction,
  fetchIntakeProgress,
  saveIntakeDraftMessages,
  createIntakeSession,
  updateIntakeCapturedItem,
  resolveIntakeExtractionConflict,
} from "../../services/intakeClient";
import { determineArchetype } from "../../services/archetypeClient";
import { validateIntakeText } from "../../utils/intakeTextValidation";
import { intakeUploadSizeError, isIntakeUploadTooLarge, isIntakeUploadTypeAllowed, INTAKE_UPLOAD_TOO_LARGE_MSG, INTAKE_UPLOAD_UNSUPPORTED_MSG } from "../../utils/intakeUploadLimits";
import { formatIntakeUploadError } from "../../utils/intakeUploadErrors";
import {
  extractionPillAnchorId,
  getSectionCompletionChanges,
  shouldAttachSectionScorePill,
  stripApologyBoilerplate,
  withExtractionPillAnchors,
  resolveMessageTurn,
  messagesToTextHistory,
  dedupeIntakeTranscript,
  selectIntakeTranscriptSource,
  transcriptHasUploadCancelled,
  countIntakeFacts,
  computeScoresFromCaptured,
  computeOverallScore,
  computeIsComplete,
  appNameFromCaptured,
  applicationNameForCard,
  findAppNameItemId,
  isGate0FrozenItem,
  injectAppVersionIntoCaptured,
} from "../../utils/chatUtils";
import { updateProjectName } from "../../store/slices/projectSlice";
import { loadIntakeDraft, saveIntakeDraft, clearIntakeDraft } from "../../utils/intakeDraftStorage";
import { resolveIntakeStartedAt } from "../../utils/intakeStartedStorage";
import { loadGate0 } from "../../utils/gate0Storage";
import { INTAKE_COLORS } from "../../theme/intakeTokens";
import IntakeGateTabsBar from "../../components/intake/IntakeGateTabsBar";
import IntakeProfileHeader from "../../components/intake/IntakeProfileHeader";
import IntakeSidebar from "../../components/intake/IntakeSidebar";
import IntakeChatCenter from "../../components/intake/IntakeChatCenter";
import IntakeExtractionConflictDialog from "../../components/intake/IntakeExtractionConflictDialog";
import IntakeSuccessToast from "../../components/intake/IntakeSuccessToast";
import IntakeStartingSessionDialog from "../../components/intake/IntakeStartingSessionDialog";
const CHAT_FONT = "Lato, sans-serif";

/** Match ProjectsLayout — keep AppHeader + ProjectsSidebar visible */
const ACTIVE_GATE = 0;
const INTAKE_PAGE_SHELL = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: "100%",
  bgcolor: INTAKE_COLORS.surface,
  fontFamily: CHAT_FONT,
  overflow: "hidden",
  position: "relative",
};

const CHAT_FOOTER_SX = {
  flexShrink: 0,
  width: "100%",
  height: 56,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  px: "24px",
  bgcolor: "#FFFFFF",
  borderTop: "1px solid #E6E6E6",
  boxShadow: "0px -1px 8px 0px #2122260A",
};

const CHAT_FOOTER_BTN_SHADOW = "0px 1px 2px 0px #0000000A";
const CHAT_FOOTER_BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";

/** Footer ghost Back — same Default / Hover / Focus / Active / Disabled as Gate 0 App Identity. */
const chatFooterGhostBtnSx = {
  textTransform: "none",
  fontFamily: CHAT_FONT,
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  minWidth: 0,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  boxSizing: "border-box",
  borderRadius: "4px",
  border: "1px solid #005BF0",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  color: "#005BF0",
  boxShadow: CHAT_FOOTER_BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: CHAT_FOOTER_BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
    boxShadow: CHAT_FOOTER_BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: CHAT_FOOTER_BTN_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    color: "#BBBBBB",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    cursor: "not-allowed",
  },
};

/** Footer primary — same Default / Hover / Focus / Active as Configuration Next. */
const chatFooterPrimaryBtnSx = {
  textTransform: "none",
  fontFamily: CHAT_FONT,
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  minWidth: 0,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  boxSizing: "border-box",
  borderRadius: "4px",
  bgcolor: "#005BF0",
  backgroundColor: "#005BF0",
  backgroundImage: "none",
  color: "#FFFFFF",
  border: "1px solid transparent",
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#0046B8",
    backgroundColor: "#0046B8",
    backgroundImage: "none",
    color: "#FFFFFF",
    border: "1px solid transparent",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#005BF0 !important",
    backgroundColor: "#005BF0 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    border: "2px solid #005BF0",
    boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #005BF0",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#005BF0 !important",
    backgroundColor: "#005BF0 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    border: "2px solid #005BF0",
    boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#0046B8 !important",
    backgroundColor: "#0046B8 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    border: "1px solid transparent",
    boxShadow: CHAT_FOOTER_BTN_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    bgcolor: "#005BF0",
    color: "#FFFFFF",
    opacity: 0.6,
  },
};

const SIDEBAR_DEFAULT_WIDTH = 320;
const SIDEBAR_MIN_WIDTH = 240;
const SIDEBAR_MAX_WIDTH = 560;

/** Hover ↔ — hollow outlined arrow only, no box (matches uploaded Media__2__). */
function DividerHoverIcon({ width = 16, height = 9 }) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      {/* Sharp hollow ↔: white fill + thin dark outline, no container */}
      <path
        d="M5.75 1L1.25 4.5L5.75 8V5.75H10.25V8L14.75 4.5L10.25 1V3.25H5.75V1Z"
        fill="#FFFFFF"
        stroke="#1A1A1A"
        strokeWidth="1"
        strokeLinejoin="miter"
      />
    </svg>
  );
}

function uploadCancelledContent(filename) {
  const name = filename || "document";
  return `**File:** ${name}\n\nUpload cancelled.`;
}

function transcriptHasReply(messages, reply) {
  const text = stripApologyBoilerplate(reply || "").trim();
  if (!text) return false;
  return (messages || []).some(
    (msg) => msg?.role === "assistant"
      && stripApologyBoilerplate(msg.content || "").trim() === text
  );
}

function restoreDraftTranscript(dispatch, { localDraft, serverMessages, turns, sessionId }) {
  const localMessages = localDraft?.sessionId === sessionId ? localDraft.messages : null;
  const localById = new Map((localMessages || []).map((m) => [m.id, m]));
  // Server is the saved transcript. Local is used only when it still holds
  // every server turn and has turns that were shown but not stored yet.
  const rawSource = selectIntakeTranscriptSource(serverMessages, localMessages);
  const messages = dedupeIntakeTranscript(
    rawSource
      .map((m) => {
        const local = localById.get(m.id);
        const merged = !m.scorePill && local?.scorePill ? { ...m, scorePill: local.scorePill } : m;
        if (merged.role !== "assistant" || !merged.content) return merged;
        const cleaned = stripApologyBoilerplate(merged.content);
        return cleaned ? { ...merged, content: cleaned } : null;
      })
      .filter(Boolean)
  );
  if (messages.length) {
    // Replace atomically — StrictMode can run two bootstraps; appending would
    // duplicate the welcome message even when the session was created once.
    dispatch(setIntakeMessages(messages));
    return messages;
  }
  if (turns > 0) {
    const resume = [{
      id: `resume-${sessionId}`,
      role: "assistant",
      content: "Welcome back — continuing your intake. Your progress has been restored.",
      label: "Resume",
    }];
    dispatch(setIntakeMessages(resume));
    return resume;
  }
  return [];
}

export default function ChatPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const intake = useSelector((state) => state.intake);
  const [bootLoading, setBootLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [summaryOpening, setSummaryOpening] = useState(false);
  const summaryOpeningRef = useRef(false);

  const showToast = useCallback((message, severity = "info") => {
    if (!message) return;
    const type =
      severity === "error" || severity === "cancel"
        ? "cancel"
        : severity === "warning"
        ? "warning"
        : severity === "success"
        ? "success"
        : "info";
    setToastType(type);
    setToastMessage(message);
  }, []);

  const openSummary = useCallback(async () => {
    if (!projectId || summaryOpeningRef.current) return;
    summaryOpeningRef.current = true;
    setSummaryOpening(true);
    try {
      const res = await determineArchetype(projectId);
      if (!res?.success) {
        showToast(res?.error || "Failed to determine archetype.", "error");
      }
    } catch (e) {
      showToast(e.response?.data?.error || e.message || "Failed to determine archetype.", "error");
    } finally {
      summaryOpeningRef.current = false;
      setSummaryOpening(false);
    }
    navigate(`/summary/${projectId}`);
  }, [navigate, projectId, showToast]);
  const prevScoresRef = useRef({});
  const prevCapturedRef = useRef({});
  const turnsRef = useRef(0);
  const sectionsRef = useRef([]);
  const uploadAbortRef = useRef(null);
  const uploadInProgressRef = useRef(false);
  const uploadFilenameRef = useRef("");
  const notedCancelKeyRef = useRef("");
  const cancelRequestedRef = useRef(false);
  const messagesRef = useRef([]);
  const scorePillsRef = useRef([]);
  // True from the moment a chat request starts until its reply is applied.
  // The server write of that turn includes the reply; a client write of the
  // user-only list in between would replace it.
  const chatPendingRef = useRef(false);
  const pendingServerSaveRef = useRef(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const prevIsCompleteRef = useRef(false);
  const completePromptShownRef = useRef(false);
  const [showCompletePrompt, setShowCompletePrompt] = useState(false);
  const [extractionConflicts, setExtractionConflicts] = useState([]);
  const [activeConflictIndex, setActiveConflictIndex] = useState(0);
  const [resolvingConflict, setResolvingConflict] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);
  const [isSidebarDragging, setIsSidebarDragging] = useState(false);
  const [isSidebarHovering, setIsSidebarHovering] = useState(false);
  const [dividerHandleY, setDividerHandleY] = useState(0);
  const sidebarDragRef = useRef(null);
  const sidebarDividerRef = useRef(null);


  const draftSaveChainRef = useRef(Promise.resolve());

  const postDraftToServer = useCallback((pid, messages) => {
    pendingServerSaveRef.current = null;
    const snapshot = messages;
    // Queue writes so an older request cannot finish after a newer transcript and replace it.
    draftSaveChainRef.current = draftSaveChainRef.current.then(() => (
      saveIntakeDraftMessages(pid, snapshot).catch((err) => {
        console.warn("[intake] draft save:", err?.message || err);
      })
    ));
  }, []);

  const persistDraft = useCallback((pid, sessionId, messages, scorePills) => {
    if (!pid || !sessionId) return;
    saveIntakeDraft(pid, { sessionId, messages, scorePills, updatedAt: Date.now() });
    // Hold the server write while a chat reply or an upload result is still
    // missing. Post as soon as that result is on the list.
    if (chatPendingRef.current || uploadInProgressRef.current) {
      pendingServerSaveRef.current = { pid, messages };
      return;
    }
    postDraftToServer(pid, messages);
  }, [postDraftToServer]);

  const syncProjectNameFromEdit = useCallback((rawValue) => {
    const name = applicationNameForCard(rawValue);
    if (!name || !projectId) return;
    dispatch(updateProjectName({ id: parseInt(projectId, 10), project_name: name }));
    dispatch(setIntakeProject({ projectId: parseInt(projectId, 10), projectName: name }));
  }, [dispatch, projectId]);

  const applyAgentResponse = useCallback((data, fromExtraction = false) => {
    const prevCaptured = { ...prevCapturedRef.current };
    const sections = data.progress?.framework_sections || sectionsRef.current || [];

    let nextScores = { ...prevScoresRef.current };
    let sectionsForPills = sections;
    let mergedCaptured = null;

    if (data.progress) {
      const merged = { ...data.progress };
      const rawCaptured = data.captured || {};
      const rawScores = data.scores || {};
      // Prefer progress.captured (Node-transformed). Still merge any non-empty
      // extracted/inferred values from the raw agent payload that transformProgress
      // may have dropped historically — document-backed answers must survive.
      const progressCaptured = merged.captured || {};
      const baseCaptured =
        Object.keys(progressCaptured).length > 0
          ? { ...progressCaptured }
          : { ...rawCaptured };
      if (fromExtraction || Object.keys(rawCaptured).length > 0) {
        Object.entries(rawCaptured).forEach(([iid, cap]) => {
          if (!cap || typeof cap !== "object") return;
          const src = cap.source;
          const val = String(cap.value ?? "").trim();
          if (!val) return;
          if (src !== "extracted" && src !== "inferred") return;
          if (!baseCaptured[iid] || !String(baseCaptured[iid].value ?? "").trim()) {
            baseCaptured[iid] = { ...cap };
          }
        });
      }
      merged.captured = injectAppVersionIntoCaptured(
        baseCaptured,
        merged.framework_sections || sections,
        merged.gate0_data || data.gate0_data
      );
      merged.scores = { ...(merged.scores || {}), ...rawScores };
      sectionsForPills = merged.framework_sections?.length
        ? merged.framework_sections
        : sections;
      const replaceCaptured =
        fromExtraction
        || "captured" in data
        || "captured" in (data.progress || {});
      merged.replace_captured = replaceCaptured;
      const computed = computeScoresFromCaptured(merged.captured, sectionsForPills);
      sectionsForPills.forEach((s) => {
        if (replaceCaptured) {
          merged.scores[s.id] =
            rawScores[s.id] ?? merged.scores[s.id] ?? computed[s.id] ?? 0;
        } else {
          merged.scores[s.id] = Math.max(
            computed[s.id] || 0,
            merged.scores[s.id] || 0,
            rawScores[s.id] || 0
          );
        }
      });
      merged.overall_score = computeOverallScore(merged.scores, sectionsForPills);
      merged.is_complete = computeIsComplete(merged.scores, sectionsForPills, merged.captured);
      mergedCaptured = merged.captured;
      dispatch(applyIntakeState(merged));
      if (merged.framework_sections?.length) {
        sectionsRef.current = merged.framework_sections;
      }
      nextScores = merged.scores;
      prevScoresRef.current = nextScores;
      prevCapturedRef.current = { ...mergedCaptured };
    } else if (data.scores) {
      const captured = data.captured || {};
      const replaceCaptured = fromExtraction || "captured" in data;
      const computed = computeScoresFromCaptured(captured, sections);
      const scores = {};
      sections.forEach((s) => {
        scores[s.id] = replaceCaptured
          ? (data.scores[s.id] ?? computed[s.id] ?? 0)
          : Math.max(computed[s.id] || 0, data.scores[s.id] || 0);
      });
      mergedCaptured = captured;
      dispatch(applyIntakeState({
        scores,
        captured,
        replace_captured: replaceCaptured,
        overall_score: computeOverallScore(scores, sections),
        is_complete: computeIsComplete(scores, sections, captured),
        turns: data.turns,
        framework_sections: sections,
      }));
      nextScores = scores;
      prevScoresRef.current = scores;
      prevCapturedRef.current = { ...captured };
    }

    const changes = getSectionCompletionChanges(
      prevCaptured,
      mergedCaptured ?? prevCaptured,
      sectionsForPills
    );
    const hasRegression = changes.some((c) => c.diff < 0);
    if (hasRegression) {
      dispatch(clearScorePills());
    }
    const improved = changes.filter((c) => c.diff > 0);
    // One pill for this turn. When this turn adds a reply, the pill is stored
    // on that message. When it does not (upload result already in the transcript),
    // anchorMessageId keeps the pill under the extraction turn so later chat
    // messages do not carry it downward.
    // A rejected or off-topic turn still applies captured above so the sidebar
    // catches up, but it must not claim this message caused the coverage jump.
    let turnPill = null;
    const replyMessageId = data.reply ? `bot-${Date.now()}` : null;
    if (
      improved.length > 0
      && shouldAttachSectionScorePill({
        fromExtraction,
        routingOutcome: data.routing_outcome,
        capturedIds: data.captured_ids,
        guardrail: data.guardrail,
        reply: data.reply,
      })
    ) {
      const existingMessages = messagesRef.current || [];
      const anchorMessageId = replyMessageId
        || extractionPillAnchorId(existingMessages)
        || existingMessages[existingMessages.length - 1]?.id
        || null;
      turnPill = {
        id: `pill-${Date.now()}`,
        kind: "section",
        changes: improved,
        prefix: fromExtraction ? "Extracted" : undefined,
        subtle: !fromExtraction,
        anchorMessageId,
      };
      // Keep earlier extraction pills. A later chat turn used to replace the
      // whole list, which dropped the extracted pill or left it at the bottom.
      const priorExtracted = hasRegression
        ? []
        : (scorePillsRef.current || []).filter(
          (pill) => pill?.prefix === "Extracted" && pill.id !== turnPill.id
        );
      dispatch(setScorePills([...priorExtracted, turnPill]));
    }

    if (data.reply) {
      dispatch(addIntakeMessage({
        id: replyMessageId,
        role: "assistant",
        content: stripApologyBoilerplate(data.reply),
        ...(turnPill ? { scorePill: turnPill } : {}),
      }));
    }

    if (data.outputs && !data.outputs.error) {
      dispatch(addIntakeMessage({
        id: `outputs-${Date.now()}`,
        role: "assistant",
        content: "",
        outputs: data.outputs,
      }));
    }
  }, [dispatch]);

  const openExtractionConflicts = useCallback((conflicts) => {
    if (!Array.isArray(conflicts) || !conflicts.length) return;
    setExtractionConflicts(conflicts);
    setActiveConflictIndex(0);
  }, []);

  useEffect(() => {
    messagesRef.current = intake.messages || [];
  }, [intake.messages]);

  useEffect(() => {
    scorePillsRef.current = intake.scorePills || [];
  }, [intake.scorePills]);

  // Older sessions stored the extracted pill with no message. Pin it once so
  // a later upload does not become its anchor.
  useEffect(() => {
    const next = withExtractionPillAnchors(intake.messages, intake.scorePills);
    if (next === intake.scorePills) return;
    dispatch(setScorePills(next));
  }, [dispatch, intake.messages, intake.scorePills]);

  const noteUploadCancelled = useCallback((filename, knownMessages) => {
    const key = filename || "document";
    if (notedCancelKeyRef.current === key) return;
    const messages = knownMessages || messagesRef.current;
    if (transcriptHasUploadCancelled(messages, filename)) {
      notedCancelKeyRef.current = key;
      return;
    }
    notedCancelKeyRef.current = key;
    dispatch(addIntakeMessage({
      id: `upload-cancel-${Date.now()}`,
      role: "assistant",
      label: "Upload cancelled",
      content: uploadCancelledContent(filename),
      isError: false,
    }));
  }, [dispatch]);

  const applyFinishedUpload = useCallback((payload, knownMessages) => {
    const messages = knownMessages || messagesRef.current;
    const reply = payload?.reply;
    const next = reply && transcriptHasReply(messages, reply)
      ? { ...payload, reply: undefined }
      : payload;
    const n = next?.captured_count
      ?? Object.keys(next?.captured || next?.progress?.captured || {}).length;
    if (next?.extraction_conflicts?.length) {
      showToast(
        `Document processed — ${next.extraction_conflicts.length} field conflict${
          next.extraction_conflicts.length === 1 ? "" : "s"
        } need your review.`,
        "warning"
      );
    } else if (n > 0) {
      showToast(
        `Document processed — ${n} framework items captured. See Collected Data and section scores.`,
        "success"
      );
    }
    applyAgentResponse(next, true);
    openExtractionConflicts(next?.extraction_conflicts);
  }, [applyAgentResponse, openExtractionConflicts, showToast]);

  const applyIfCommitted = useCallback(async (projectId, sessionId, payload) => {
    if (
      payload?.status === "cancelled"
      || payload?.extraction_strategy === "CANCELLED"
    ) {
      noteUploadCancelled(payload.filename || uploadFilenameRef.current);
      return;
    }
    if (cancelRequestedRef.current) return;
    let latest = payload;
    if (projectId && sessionId) {
      try {
        latest = await fetchIntakeUploadStatus(projectId, sessionId);
      } catch (err) {
        console.warn("[intake] upload-status before apply:", err?.message || err);
        return;
      }
    }
    if (cancelRequestedRef.current || latest?.status === "cancelled") {
      if (latest?.status === "cancelled") {
        noteUploadCancelled(latest.filename || payload?.filename || uploadFilenameRef.current);
      }
      return;
    }
    if (latest?.status === "complete") {
      applyFinishedUpload(latest);
      return;
    }
    // Sync upload returns the saved result directly and does not leave a job.
    if (!payload?.status && (payload?.progress || payload?.captured)) {
      applyFinishedUpload(payload);
    }
  }, [applyFinishedUpload, noteUploadCancelled]);

  const followUploadJob = useCallback(async (pid, sessionId, controller, isStale) => {
    try {
      const result = await pollUploadStatus(pid, sessionId, {
        signal: controller.signal,
      });
      if (isStale()) return;
      await applyIfCommitted(pid, sessionId, result);
    } catch (err) {
      if (isStale()) return;
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || axios.isCancel?.(err)) {
        return;
      }
      const errorInfo = formatIntakeUploadError(err, uploadFilenameRef.current);
      dispatch(addIntakeMessage({
        id: `upload-err-${Date.now()}`,
        role: "assistant",
        content: errorInfo.chatMessage,
        label: errorInfo.chatLabel || "Upload failed",
        isError: !errorInfo.isGuidance,
      }));
      showToast(errorInfo.message, errorInfo.isGuidance ? "warning" : "error", { persistent: true });
    } finally {
      if (isStale()) return;
      if (uploadAbortRef.current === controller) uploadAbortRef.current = null;
      uploadInProgressRef.current = false;
      setUploadInProgress(false);
      setChatLoading(false);
    }
  }, [applyIfCommitted, dispatch, noteUploadCancelled, showToast]);

  const attachInFlightUpload = useCallback(async (pid, sessionId, isStale, knownMessages) => {
    let status;
    try {
      status = await fetchIntakeUploadStatus(pid, sessionId);
    } catch (err) {
      console.warn("[intake] upload-status:", err?.message || err);
      return;
    }
    if (isStale()) return;
    if (status.status === "processing") {
      uploadFilenameRef.current = status.filename || uploadFilenameRef.current;
      const controller = new AbortController();
      uploadAbortRef.current = controller;
      uploadInProgressRef.current = true;
      setUploadInProgress(true);
      void followUploadJob(pid, sessionId, controller, isStale);
      return;
    }
    if (status.status === "complete") {
      if (cancelRequestedRef.current) return;
      const messages = knownMessages || messagesRef.current;
      // Reply already in the transcript means this result was applied (or saved
      // on the server). Do not replace captured again — later chat edits would be lost.
      if (status.reply && transcriptHasReply(messages, status.reply)) return;
      applyFinishedUpload(status, messages);
      return;
    }
    if (status.status === "cancelled") {
      noteUploadCancelled(status.filename, knownMessages || messagesRef.current);
    }
  }, [applyFinishedUpload, followUploadJob, noteUploadCancelled]);

  // Apply Keep current / Use new; update sidebar. Chat-origin follow-up was
  // already posted on the conflict turn — res.reply is usually absent.
  const handleResolveExtractionConflict = useCallback(async (choice) => {
    const conflict = extractionConflicts[activeConflictIndex];
    if (!conflict || !intake.sessionId || !intake.projectId) return;
    setResolvingConflict(true);
    try {
      const res = await resolveIntakeExtractionConflict(
        intake.projectId,
        intake.sessionId,
        conflict,
        choice
      );
      applyAgentResponse(
        {
          progress: res.progress,
          captured: res.progress?.captured,
          scores: res.progress?.scores,
          // Optional; chat-origin resolve no longer returns a follow-up reply.
          reply: res.reply,
        },
        true
      );
      const remaining = res.extraction_conflicts || [];
      if (remaining.length) {
        setExtractionConflicts(remaining);
        setActiveConflictIndex(0);
      } else {
        setExtractionConflicts([]);
        setActiveConflictIndex(0);
        showToast("All extraction conflicts reviewed.", "success");
      }
    } catch (err) {
      showToast(err.response?.data?.error || err.message || "Could not resolve conflict", "error");
    } finally {
      setResolvingConflict(false);
    }
  }, [
    activeConflictIndex,
    applyAgentResponse,
    extractionConflicts,
    intake.projectId,
    intake.sessionId,
  ]);

  const recoverExpiredSession = useCallback(async () => {
    clearIntakeDraft(intake.projectId);
    dispatch(clearIntakeTranscript());
    const fresh = await createIntakeSession(intake.projectId);
    dispatch(setIntakeSession({ sessionId: fresh.session_id }));
    if (fresh.progress) {
      dispatch(applyIntakeState({ ...fresh.progress, replace_captured: true }));
      sectionsRef.current = fresh.progress.framework_sections || [];
      prevScoresRef.current = fresh.progress.scores || {};
      prevCapturedRef.current = fresh.progress.captured || {};
    }
    if (fresh.reply) {
      dispatch(addIntakeMessage({
        id: `bot-recover-${Date.now()}`,
        role: "assistant",
        content: stripApologyBoilerplate(fresh.reply),
      }));
    }
    return fresh.session_id;
  }, [dispatch, intake.projectId]);

  const handleIntakeError = useCallback(async (err, fallbackMsg) => {
    const code = err.response?.data?.code;
    if (err.response?.status === 410 || code === "SESSION_EXPIRED") {
      try {
        await recoverExpiredSession();
        showToast("Session reconnected — please send your message again.", "info");
        return true;
      } catch {
        showToast("Session expired — please refresh the page.", "error");
        return true;
      }
    }
    showToast(
      intakeUploadSizeError(err)
        || (typeof err.response?.data?.error === "string" && err.response.data.error !== "Intake agent error"
          ? err.response.data.error
          : null)
        || (typeof err.response?.data?.detail === "string" ? err.response.data.detail : null)
        || (err.response?.data?.detail?.message)
        || err.response?.data?.message
        || fallbackMsg,
      "error"
    );
    return false;
  }, [recoverExpiredSession, showToast]);

  const bootstrapSession = useCallback(async (isStale = () => false) => {
    try {
      setBootLoading(true);
      dispatch(setIntakeError(null));

      if (!projectId || projectId === "new") {
        // No project context — start from Home (use +Onboard to create one).
        if (!isStale()) navigate("/home", { replace: true });
        return;
      }

      const pid = projectId;

      const projectRes = await axios.get(`${API_BASE_URL}/projects/${pid}`).catch(() => null);
      if (isStale()) return;
      dispatch(setIntakeProject({
        projectId: pid,
        projectName: projectRes?.data?.data?.project_name || "",
      }));

      let sessionId;
      let restoredMessages = [];
      const existing = await getIntakeSession(pid);
      if (isStale()) return;
      if (existing?.data?.session_id) {
        sessionId = existing.data.session_id;
        const prog = await fetchIntakeProgress(pid, sessionId);
        if (isStale()) return;
        if (prog.success && prog.data) {
          const startedAt = resolveIntakeStartedAt(pid, {
            progressStartedAt: prog.data.started_at,
            sessionStartedAt: existing.data.started_at,
          });
          const gate0ForVersion =
            prog.data.gate0_data
            || loadGate0(pid)
            || projectRes?.data?.data?.gate0_data;
          const captured = injectAppVersionIntoCaptured(
            prog.data.captured,
            prog.data.framework_sections,
            gate0ForVersion
          );
          dispatch(applyIntakeState({
            ...prog.data,
            captured,
            gate0_data: gate0ForVersion || prog.data.gate0_data,
            started_at: startedAt,
            replace_captured: true,
            open_sections: [],
          }));
          sectionsRef.current = prog.data.framework_sections || [];
          prevScoresRef.current = prog.data.scores || {};
          prevCapturedRef.current = captured;
          if (prog.data.turns) turnsRef.current = prog.data.turns;

          const localDraft = loadIntakeDraft(pid);
          restoredMessages = restoreDraftTranscript(dispatch, {
            localDraft,
            serverMessages: existing.data.draft_messages || prog.data.draft_messages,
            turns: prog.data.turns || 0,
            sessionId,
          }) || [];
          if (localDraft?.sessionId === sessionId && Array.isArray(localDraft.scorePills)) {
            dispatch(setScorePills(localDraft.scorePills));
          }
        }
      } else {
        const gate0Payload = location.state?.gate0 || loadGate0(pid);
        try {
          const created = await createIntakeSession(pid, gate0Payload || undefined);
          if (isStale()) return;
          if (created?.session_id) {
            sessionId = created.session_id;
            if (created.progress) {
              const startedAt = resolveIntakeStartedAt(pid, {
                progressStartedAt: created.progress.started_at,
              });
              dispatch(applyIntakeState({
                ...created.progress,
                started_at: startedAt,
                replace_captured: true,
                open_sections: [],
              }));
              sectionsRef.current = created.progress.framework_sections || [];
              prevScoresRef.current = created.progress.scores || {};
              prevCapturedRef.current = created.progress.captured || {};
            }
            if (created.reply) {
              restoredMessages = [{
                id: `welcome-${sessionId}`,
                role: "assistant",
                content: created.reply,
                label: "Welcome",
              }];
              dispatch(setIntakeMessages(restoredMessages));
            } else if (Array.isArray(created.draft_messages) && created.draft_messages.length) {
              // Resumed create (session already started e.g. from framework page)
              // may return draft without reply — restore once, do not append.
              restoredMessages = restoreDraftTranscript(dispatch, {
                localDraft: loadIntakeDraft(pid),
                serverMessages: created.draft_messages,
                turns: created.turns || created.progress?.turns || 0,
                sessionId,
              }) || [];
            }
          } else {
            navigate(`/intake/${pid}`, { replace: true });
            return;
          }
        } catch (createErr) {
          if (isStale()) return;
          if (
            createErr.response?.status === 403 &&
            createErr.response?.data?.error === "Forbidden" &&
            createErr.response?.data?.message === "You do not have access to this project."
          ) {
            dispatch(setIntakeError(createErr.response.data.message));
            return;
          }
          navigate(`/intake/${pid}`, { replace: true });
          return;
        }
      }

      if (isStale()) return;
      dispatch(setIntakeSession({ sessionId }));
      dispatch(setIntakeStatus("ready"));
      if (sessionId) {
        messagesRef.current = restoredMessages;
        await attachInFlightUpload(pid, sessionId, isStale, restoredMessages);
      }
    } catch (err) {
      if (isStale()) return;
      const data = err.response?.data;
      const accessDenied =
        err.response?.status === 403 &&
        data?.error === "Forbidden" &&
        data?.message === "You do not have access to this project.";
      const msg = accessDenied
        ? data.message
        : (data?.error || data?.detail || err.message || "Failed to start intake session");
      dispatch(setIntakeError(typeof msg === "string" ? msg : JSON.stringify(msg)));
    } finally {
      if (!isStale()) setBootLoading(false);
    }
  }, [attachInFlightUpload, dispatch, projectId, navigate, location.state]);

  useEffect(() => {
    let cancelled = false;
    dispatch(resetIntake());
    bootstrapSession(() => cancelled);
    return () => {
      cancelled = true;
      uploadAbortRef.current?.abort();
    };
    // Only re-bootstrap when the project changes (not when bootstrapSession identity changes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (intake.turns) turnsRef.current = intake.turns;
  }, [intake.turns]);

  useEffect(() => {
    if (bootLoading) {
      prevIsCompleteRef.current = intake.isComplete;
      return;
    }
    const wasComplete = prevIsCompleteRef.current;
    if (!wasComplete && intake.isComplete && !completePromptShownRef.current) {
      completePromptShownRef.current = true;
      setShowCompletePrompt(true);
    }
    prevIsCompleteRef.current = intake.isComplete;
  }, [intake.isComplete, bootLoading]);

  useEffect(() => {
    if (intake.frameworkSections?.length) {
      sectionsRef.current = intake.frameworkSections;
    }
    if (intake.scores && Object.keys(intake.scores).length) {
      prevScoresRef.current = intake.scores;
    }
    if (intake.captured && Object.keys(intake.captured).length) {
      prevCapturedRef.current = intake.captured;
    }
  }, [intake.frameworkSections, intake.scores, intake.captured]);

  useEffect(() => {
    if (!intake.projectId || !intake.sessionId || intake.status !== "ready") return;
    persistDraft(intake.projectId, intake.sessionId, intake.messages, intake.scorePills);
  }, [
    intake.projectId,
    intake.sessionId,
    intake.messages,
    intake.scorePills,
    intake.status,
    persistDraft,
  ]);

  const updateDividerHandleY = useCallback((clientY) => {
    const el = sidebarDividerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const y = Math.min(Math.max(clientY - rect.top, 0), rect.height);
    setDividerHandleY(y);
  }, []);

  useEffect(() => {
    const onPointerMove = (e) => {
      if (!sidebarDragRef.current) return;
      const dx = e.clientX - sidebarDragRef.current.startX;
      const next = Math.min(
        SIDEBAR_MAX_WIDTH,
        Math.max(SIDEBAR_MIN_WIDTH, sidebarDragRef.current.startWidth + dx)
      );
      setSidebarWidth(next);
      // Keep ↔ at pointer while dragging (same as browser col-resize cursor).
      updateDividerHandleY(e.clientY);
    };
    const onPointerUp = (e) => {
      if (!sidebarDragRef.current) return;
      sidebarDragRef.current = null;
      setIsSidebarDragging(false);
      const el = sidebarDividerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const inside =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;
        setIsSidebarHovering(inside);
        if (inside) updateDividerHandleY(e.clientY);
      } else {
        setIsSidebarHovering(false);
      }
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [updateDividerHandleY]);

  const handleSidebarDividerPointerDown = useCallback((e) => {
    e.preventDefault();
    sidebarDragRef.current = {
      startX: e.clientX,
      startWidth: sidebarWidth,
    };
    setIsSidebarDragging(true);
    updateDividerHandleY(e.clientY);
    // Hide browser ←||→; custom ↔ handle follows pointer instead.
    document.body.style.cursor = "none";
    document.body.style.userSelect = "none";
  }, [sidebarWidth, updateDividerHandleY]);

  const handleSidebarDividerPointerEnter = useCallback((e) => {
    setIsSidebarHovering(true);
    updateDividerHandleY(e.clientY);
  }, [updateDividerHandleY]);

  const handleSidebarDividerPointerMove = useCallback((e) => {
    if (sidebarDragRef.current) return;
    setIsSidebarHovering(true);
    updateDividerHandleY(e.clientY);
  }, [updateDividerHandleY]);

  const handleSidebarDividerPointerLeave = useCallback(() => {
    if (sidebarDragRef.current) return;
    setIsSidebarHovering(false);
  }, []);

  useEffect(() => () => {
    // A request still in flight saves the user turn and the reply together.
    // Flushing the user-only list here would replace that reply.
    if (chatPendingRef.current) return;
    const pending = pendingServerSaveRef.current;
    if (!pending?.pid) return;
    postDraftToServer(pending.pid, pending.messages);
  }, [postDraftToServer]);

  const beginChatTurn = (messages) => {
    chatPendingRef.current = true;
    pendingServerSaveRef.current = { pid: intake.projectId, messages };
    saveIntakeDraft(intake.projectId, {
      sessionId: intake.sessionId,
      messages,
      scorePills: intake.scorePills,
      updatedAt: Date.now(),
    });
  };

  const finishChatTurn = (turnSaved) => {
    chatPendingRef.current = false;
    if (turnSaved) {
      // The chat handler already stored the reply. Drop the user-only snapshot
      // so unmount does not write it back over that reply.
      pendingServerSaveRef.current = null;
      return;
    }
    const pending = pendingServerSaveRef.current;
    if (!pending?.pid) return;
    postDraftToServer(pending.pid, pending.messages);
  };

  const handleSendChat = async (message) => {
    if (!intake.sessionId || !intake.projectId) return;
    const validated = validateIntakeText(message);
    if (!validated.ok) {
      showToast(validated.message, "error");
      return false;
    }
    turnsRef.current += 1;
    const turn = turnsRef.current;
    const userMsg = {
      id: `user-${Date.now()}`,
      role: "user",
      source: "typed",
      content: message,
      turn,
    };
    const draftMessages = [...intake.messages, userMsg];
    beginChatTurn(draftMessages);
    setChatLoading(true);
    dispatch(addIntakeMessage(userMsg));
    let turnSaved = false;
    try {
      const res = await sendIntakeChat(intake.projectId, intake.sessionId, message, draftMessages);
      applyAgentResponse(res, false);
      turnSaved = true;
      openExtractionConflicts(res.extraction_conflicts);
    } catch (err) {
      await handleIntakeError(err, err.message || "Chat failed");
    } finally {
      finishChatTurn(turnSaved);
      setChatLoading(false);
    }
    return true;
  };

  const handleEditCapturedItem = async (item, newValue) => {
    if (isGate0FrozenItem(item, intake.gate0Data, intake.captured)) {
      const msg = "This value comes from App Identity and cannot be edited here.";
      showToast(msg, "error");
      throw new Error(msg);
    }
    const validated = validateIntakeText(newValue);
    if (!validated.ok) {
      showToast(validated.message, "error");
      throw new Error(validated.message);
    }
    if (!intake.sessionId || !intake.projectId) return;
    try {
      const res = await updateIntakeCapturedItem(
        intake.projectId,
        intake.sessionId,
        item.id,
        validated.value
      );
      applyAgentResponse(
        {
          progress: res.progress,
          captured: res.progress?.captured,
          scores: res.progress?.scores,
        },
        false
      );
      const appNameItemId = findAppNameItemId(intake.frameworkSections);
      if (item.id === appNameItemId) {
        syncProjectNameFromEdit(validated.value);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Update failed.";
      showToast(msg, "error");
      throw new Error(msg);
    }
  };

  const handleEditMessage = async (messageId, newContent) => {
    if (!intake.sessionId || !intake.projectId) return;
    const validated = validateIntakeText(newContent);
    if (!validated.ok) {
      showToast(validated.message, "error");
      return;
    }
    const msgIndex = intake.messages.findIndex((m) => m.id === messageId);
    if (msgIndex < 0) return;

    const turn = resolveMessageTurn(intake.messages, msgIndex);
    const truncated = intake.messages.slice(0, msgIndex);

    const userMsg = {
      id: `user-${Date.now()}`,
      role: "user",
      source: "typed",
      content: newContent,
      turn,
    };
    const draftMessages = [...truncated, userMsg];
    beginChatTurn(draftMessages);
    dispatch(truncateIntakeFromMessage(messageId));
    dispatch(addIntakeMessage(userMsg));

    setChatLoading(true);
    let turnSaved = false;
    try {
      const textHistory = messagesToTextHistory(truncated);
      const res = await sendIntakeChat(
        intake.projectId,
        intake.sessionId,
        newContent,
        draftMessages,
        { rewindFromTurn: turn, textHistory },
      );
      turnsRef.current = res.turns ?? res.progress?.turns ?? turn;
      applyAgentResponse(res, false);
      turnSaved = true;
    } catch (err) {
      await handleIntakeError(err, err.message || "Edit failed");
    } finally {
      finishChatTurn(turnSaved);
      setChatLoading(false);
    }
  };

  const handlePaste = async (content) => {
    if (!intake.sessionId || !intake.projectId) return;
    turnsRef.current += 1;
    uploadFilenameRef.current = "Pasted text";
    cancelRequestedRef.current = false;
    uploadAbortRef.current = new AbortController();
    uploadInProgressRef.current = true;
    setUploadInProgress(true);
    setChatLoading(true);
    dispatch(addIntakeMessage({
      id: `paste-${Date.now()}`,
      role: "user",
      content: `📋 Processing pasted document (${content.length.toLocaleString()} chars)...`,
    }));
    try {
      const res = await pasteIntakeDocument(intake.projectId, intake.sessionId, content, {
        signal: uploadAbortRef.current.signal,
      });
      await applyIfCommitted(intake.projectId, intake.sessionId, res);
      if (!cancelRequestedRef.current && res?.extraction_conflicts?.length) {
        showToast(
          `Document processed — ${res.extraction_conflicts.length} field conflict${
            res.extraction_conflicts.length === 1 ? "" : "s"
          } need your review.`,
          "warning"
        );
        openExtractionConflicts(res.extraction_conflicts);
      }
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      await handleIntakeError(err, "Paste extraction failed");
    } finally {
      uploadAbortRef.current = null;
      uploadInProgressRef.current = false;
      setUploadInProgress(false);
      setChatLoading(false);
    }
  };

  const cancelUpload = useCallback(async () => {
    if (!intake.projectId || !intake.sessionId) {
      showToast("Could not cancel document extraction", "error");
      return;
    }
    // Latch before the request so a poll that finishes while it is in flight
    // does not apply the extraction result.
    cancelRequestedRef.current = true;
    let data;
    try {
      data = await cancelIntakeUpload(intake.projectId, intake.sessionId);
    } catch (err) {
      cancelRequestedRef.current = false;
      const msg = err?.response?.data?.error || "Could not cancel document extraction";
      showToast(msg, "error");
      return;
    }
    if (data?.status !== "cancelled") {
      // Save already started or finished. Leave the result in place.
      cancelRequestedRef.current = false;
      return;
    }
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
      uploadAbortRef.current = null;
    }
    uploadInProgressRef.current = false;
    setUploadInProgress(false);
    setChatLoading(false);
    noteUploadCancelled(data.filename || uploadFilenameRef.current);
    showToast("Document extraction cancelled", "cancel");
  }, [intake.projectId, intake.sessionId, noteUploadCancelled, showToast]);

  const handleUpload = async (file) => {
    if (!intake.sessionId || !intake.projectId) return;
    if (uploadInProgress) return { cancelled: false, skipped: true };
    if (isIntakeUploadTooLarge(file)) {
      showToast(INTAKE_UPLOAD_TOO_LARGE_MSG, "error");
      dispatch(addIntakeMessage({
        id: `upload-err-${Date.now()}`,
        role: "assistant",
        content: INTAKE_UPLOAD_TOO_LARGE_MSG,
      }));
      return { cancelled: false, error: INTAKE_UPLOAD_TOO_LARGE_MSG };
    }
    if (!isIntakeUploadTypeAllowed(file)) {
      showToast(INTAKE_UPLOAD_UNSUPPORTED_MSG, "error");
      dispatch(addIntakeMessage({
        id: `upload-err-${Date.now()}`,
        role: "assistant",
        content: INTAKE_UPLOAD_UNSUPPORTED_MSG,
        label: "Unsupported file format",
        isError: true,
      }));
      return { cancelled: false, error: INTAKE_UPLOAD_UNSUPPORTED_MSG };
    }

    uploadFilenameRef.current = file.name;
    notedCancelKeyRef.current = "";
    cancelRequestedRef.current = false;
    uploadAbortRef.current = new AbortController();
    uploadInProgressRef.current = true;
    setUploadInProgress(true);
    turnsRef.current += 1;
    setChatLoading(true);
    dispatch(addIntakeMessage({
      id: `upload-${Date.now()}`,
      role: "user",
      content: `Uploading ${file.name} (${(file.size / 1024).toFixed(0)} KB) for extraction...`,
    }));
    try {
      const res = await uploadIntakeFile(intake.projectId, intake.sessionId, file, {
        signal: uploadAbortRef.current.signal,
      });
      let payload = res;
      if (
        intake.projectId &&
        intake.sessionId &&
        !res?.has_extraction_json &&
        !res?.extraction_json
      ) {
        try {
          const ext = await fetchIntakeExtraction(intake.projectId, intake.sessionId, {
            signal: uploadAbortRef.current?.signal,
          });
          payload = {
            ...res,
            extraction_json: ext.extraction_json,
            output_file: ext.output_file ?? res.output_file,
          };
        } catch (extErr) {
          console.warn("[intake] full extraction_json fetch:", extErr?.message || extErr);
        }
      } else if (res?.extraction_json) {
        payload = res;
      }
      const n = payload?.captured_count ?? Object.keys(payload?.captured || {}).length;
      console.info("[intake] upload response", {
        captured_count: n,
        extraction_url: payload?.extraction_url,
        has_extraction_json: Boolean(payload?.extraction_json),
        strategy: payload?.extraction_strategy,
      });
      await applyIfCommitted(intake.projectId, intake.sessionId, payload);
      return { cancelled: payload?.status === "cancelled" || cancelRequestedRef.current };
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
        return { cancelled: true };
      }
      const errorInfo = formatIntakeUploadError(err, file.name);
      dispatch(addIntakeMessage({
        id: `upload-err-${Date.now()}`,
        role: "assistant",
        content: errorInfo.chatMessage,
        label: errorInfo.chatLabel || "Upload failed",
        isError: !errorInfo.isGuidance,
      }));
      showToast(errorInfo.message, errorInfo.isGuidance ? "warning" : "error", { persistent: true });
    } finally {
      uploadAbortRef.current = null;
      uploadInProgressRef.current = false;
      setUploadInProgress(false);
      setChatLoading(false);
    }
  };

  const gate0Count = intake.gate0Items
    || Object.values(intake.captured || {}).filter((c) => c.source === "gate0").length;
  const facts = countIntakeFacts(intake.frameworkSections, intake.captured);
  if (bootLoading) {
    return (
      <Box sx={INTAKE_PAGE_SHELL}>
        <IntakeStartingSessionDialog open />
      </Box>
    );
  }

  if (intake.error) {
    const accessDenied = intake.error === "You do not have access to this project.";
    return (
      <Box sx={{ ...INTAKE_PAGE_SHELL, alignItems: "center", justifyContent: "center", p: 3 }}>
        <Box sx={{ bgcolor: "#fff", borderRadius: "12px", p: 4, maxWidth: accessDenied ? 520 : 420, textAlign: "center", border: `1px solid ${INTAKE_COLORS.border}` }}>
          {accessDenied ? (
            <>
              <LockOutlinedIcon sx={{ fontSize: 28, color: INTAKE_COLORS.primary, mb: 1.5 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: INTAKE_COLORS.text, mb: 1 }}>
                You do not have access to this project
              </Typography>
              <Typography sx={{ fontSize: 13, color: INTAKE_COLORS.g5, mb: 2.5 }}>
                You don&apos;t have permission to view this project. If you believe this is a mistake, please contact your administrator.
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", mb: 2 }}>
                <Button variant="contained" onClick={() => navigate("/home")} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", bgcolor: INTAKE_COLORS.primary, "&:hover": { bgcolor: INTAKE_COLORS.primaryDark } }}>
                  Go to Projects
                </Button>
                <Button variant="outlined" onClick={() => navigate(-1)} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", color: INTAKE_COLORS.text, borderColor: INTAKE_COLORS.border }}>
                  Go Back
                </Button>
              </Box>
              <Typography sx={{ fontSize: 12, color: INTAKE_COLORS.g4 }}>
                If you believe this is an error, please contact your project administrator.
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: INTAKE_COLORS.red, mb: 1 }}>Connection Error</Typography>
              <Typography sx={{ fontSize: 13, color: INTAKE_COLORS.g5, mb: 2 }}>{intake.error}</Typography>
              <Typography sx={{ fontSize: 12, color: INTAKE_COLORS.g4, mb: 2 }}>
                Make sure the intake agent is running: cd modules/project-intake/agent && .\start.ps1
              </Typography>
              <Box component="button" onClick={() => navigate("/home")} sx={{ border: "none", bgcolor: "transparent", color: INTAKE_COLORS.blue, fontWeight: 600, cursor: "pointer", mr: 2 }}>
                ← Back to Home
              </Box>
              <Box component="button" onClick={() => window.location.reload()} sx={{ border: "none", bgcolor: "transparent", color: INTAKE_COLORS.g4, fontSize: 12, cursor: "pointer" }}>
                Retry
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={INTAKE_PAGE_SHELL}>
        <IntakeGateTabsBar
          activeGate={ACTIVE_GATE}
          unlockedUpTo={intake.isComplete ? 1 : 0}
          onGateClick={(gateIndex) => {
            if (gateIndex === 0) return; // already here
            if (gateIndex === 1 && intake.isComplete) {
              navigate(`/gate1/${projectId}`);
            }
          }}
          showBack={false}
          showViewSummary={false}
        />

        <IntakeProfileHeader
          appName={
            appNameFromCaptured(intake.captured, intake.frameworkSections)
            || intake.projectName
            || intake.gate0Data?.app_name
          }
          startedAt={intake.startedAt}
          overallPct={intake.overallScore}
          factsFilled={facts.filled}
          factsTotal={facts.total}
          projectId={projectId}
          onBack={() => navigate(`/framework/${projectId}`)}
        />

        <Box
          sx={{
            display: "flex",
            flex: 1,
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          <IntakeSidebar
            key={`${projectId}-${intake.sessionId || "none"}`}
            width={sidebarWidth}
            sections={intake.frameworkSections}
            captured={intake.captured}
            gate0Data={intake.gate0Data}
            onEditItem={handleEditCapturedItem}
            onEditSuccess={(label) => showToast(`${label} updated successfully.`, "success")}
            editDisabled={chatLoading || bootLoading}
          />

          <Box
            ref={sidebarDividerRef}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize left panel and chat"
            onPointerDown={handleSidebarDividerPointerDown}
            onPointerEnter={handleSidebarDividerPointerEnter}
            onPointerMove={handleSidebarDividerPointerMove}
            onPointerLeave={handleSidebarDividerPointerLeave}
            data-active={isSidebarHovering || isSidebarDragging ? "true" : undefined}
            sx={{
              width: 16,
              flexShrink: 0,
              position: "relative",
              cursor: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "transparent",
              touchAction: "none",
              zIndex: 2,
              // Divider line — resting 1px gray; hover 0.5px blue + transparent glow (Claude-style)
              "&::before": {
                content: '""',
                position: "absolute",
                left: "50%",
                top: 0,
                bottom: 0,
                width: "1px",
                bgcolor: "#E5E5E5",
                boxShadow: "none",
                transform: "translateX(-50%)",
                pointerEvents: "none",
                transition: "background-color 0.12s ease, width 0.12s ease, box-shadow 0.12s ease",
              },
              "&[data-active='true']::before": {
                width: "0.5px",
                bgcolor: "#005BF0",
                boxShadow: "none",
              },
              // Pull thumb — fixed size (hover dims: 7×22) before AND after hover
              "& .splitter-thumb": {
                position: "relative",
                zIndex: 1,
                width: 7,
                height: 22,
                borderRadius: "999px",
                bgcolor: "#FFFFFF",
                border: "1px solid rgba(0, 0, 0, 0.10)",
                boxShadow: "0 0 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)",
                pointerEvents: "none",
                transition:
                  "background-color 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease",
              },
              // Hover: same size — only background + outline change
              "&[data-active='true'] .splitter-thumb": {
                bgcolor: "#D8E4F5",
                border: "1px solid #8BAAD9",
                boxShadow: "0 0 2px rgba(0, 91, 240, 0.18)",
              },
              // Hover ↔ — naked outlined arrow only (matches uploaded image; no box)
              "& .divider-hover-icon": {
                position: "absolute",
                left: "50%",
                zIndex: 3,
                display: "none",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "transparent",
                border: "none",
                boxShadow: "none",
                borderRadius: 0,
                padding: 0,
                pointerEvents: "none",
                transform: "translate(-50%, -50%)",
              },
              "&[data-active='true'] .divider-hover-icon": {
                display: "flex",
              },
            }}
          >
            <Box className="splitter-thumb" />
            <Box
              className="divider-hover-icon"
              style={{ top: dividerHandleY }}
            >
              <DividerHoverIcon width={16} height={9} />
            </Box>
          </Box>

          <IntakeChatCenter
            messages={intake.messages}
            scorePills={intake.scorePills || []}
            nextTarget={intake.nextTarget}
            captured={intake.captured}
            sections={intake.frameworkSections}
            loading={chatLoading}
            uploadInProgress={uploadInProgress}
            gate0PrefillCount={gate0Count}
            factsFilled={facts.filled}
            factsTotal={facts.total}
            overallPct={intake.overallScore}
            isComplete={intake.isComplete}
            projectName={
              appNameFromCaptured(intake.captured, intake.frameworkSections)
              || intake.projectName
              || intake.gate0Data?.app_name
            }
            onSendChat={handleSendChat}
            onPaste={handlePaste}
            onUpload={handleUpload}
            onEditMessage={handleEditMessage}
            onCancelUpload={cancelUpload}
            onViewSummary={openSummary}
            viewSummaryLoading={summaryOpening}
          />
        </Box>

        <Box sx={CHAT_FOOTER_SX}>
          <Button
            disableElevation
            disableRipple
            onClick={() => navigate(`/framework/${projectId}`)}
            sx={chatFooterGhostBtnSx}
          >
            Back
          </Button>
          {intake.isComplete ? (
            <Button
              variant="contained"
              color="primary"
              disableElevation
              disableRipple
              disableFocusRipple
              onClick={openSummary}
              disabled={summaryOpening}
              startIcon={summaryOpening ? <CircularProgress size={16} sx={{ color: "#FFFFFF" }} /> : null}
              sx={chatFooterPrimaryBtnSx}
            >
              View Summary
            </Button>
          ) : (
            <Box />
          )}
        </Box>
      </Box>

      <Dialog
        open={showCompletePrompt}
        onClose={() => setShowCompletePrompt(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 460, fontFamily: CHAT_FONT } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1, fontFamily: CHAT_FONT }}>
          <CheckCircleIcon sx={{ color: INTAKE_COLORS.green }} />
          <Typography component="span" sx={{ fontSize: 18, fontWeight: 800, color: INTAKE_COLORS.black, fontFamily: CHAT_FONT }}>
            Intake complete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14, color: INTAKE_COLORS.g5, fontFamily: CHAT_FONT }}>
            All sections have reached their thresholds. Do you want to see a summary
            of everything ValidAIte gathered from this chat?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setShowCompletePrompt(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: INTAKE_COLORS.g5,
              fontFamily: CHAT_FONT,
              border: "1px solid transparent",
              "&:hover": {
                bgcolor: "#005BF014",
                border: "1px solid #005BF0",
                boxShadow: "0px 1px 2px 0px #0000000A",
              },
            }}
          >
            Stay in chat
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowCompletePrompt(false);
              openSummary();
            }}
            disabled={summaryOpening}
            startIcon={summaryOpening ? <CircularProgress size={16} sx={{ color: "#FFFFFF" }} /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              bgcolor: INTAKE_COLORS.blue,
              "&:hover": { bgcolor: INTAKE_COLORS.blueDark },
              fontFamily: CHAT_FONT,
            }}
          >
            View Summary
          </Button>
        </DialogActions>
      </Dialog>

      <IntakeExtractionConflictDialog
        open={extractionConflicts.length > 0}
        conflict={extractionConflicts[activeConflictIndex]}
        conflictIndex={activeConflictIndex}
        conflictTotal={extractionConflicts.length}
        resolving={resolvingConflict}
        onKeepCurrent={() => handleResolveExtractionConflict("keep_current")}
        onUseNew={() => handleResolveExtractionConflict("use_new")}
        onClose={() => {
          setExtractionConflicts([]);
          setActiveConflictIndex(0);
          showToast("Current mapped values have been retained.", "info");
        }}
      />

      <IntakeSuccessToast
        open={Boolean(toastMessage)}
        message={toastMessage}
        type={toastType}
        onClose={() => {
          setToastMessage("");
          setToastType("success");
        }}
      />
    </>
  );
}